import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login as apiLogin } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn, Loader2, Eye, EyeOff } from 'lucide-react';

export const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deployLogout, setDeployLogout] = useState(false);
    const { login, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user was logged out due to deploy
        const wasDeployLogout = sessionStorage.getItem('deploy_logout');
        if (wasDeployLogout) {
            setDeployLogout(true);
            sessionStorage.removeItem('deploy_logout');
        }
        
        if (user) {
            navigate(user.role === 'ADMIN' ? '/dashboard' : '/my-orders');
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await apiLogin(email, password);
            login(res.data.token, res.data.user);
            toast.success('Erfolgreich angemeldet');
            
            // Terminal accounts go directly to terminal page
            if (res.data.user.email === 'terminal@thutmanager.de') {
                navigate('/terminal');
            } else {
                navigate(res.data.user.role === 'ADMIN' ? '/dashboard' : '/my-orders');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
                style={{ 
                    backgroundImage: 'url(https://images.unsplash.com/photo-1769366529552-cc8ac4f46549?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdCUyMG1pbmltYWx8ZW58MHx8fHwxNzcxNzYyMzU4fDA&ixlib=rb-4.1.0&q=85)'
                }}
            >
                <div className="absolute inset-0 bg-primary/80" />
                <div className="relative z-10 flex flex-col justify-center p-12 text-white">
                    <h1 className="text-4xl font-bold mb-4">Fa. Thut Manager</h1>
                    <p className="text-xl text-white/80">
                        Professionelle Auftragsverwaltung für Ihr Unternehmen
                    </p>
                </div>
            </div>

            {/* Right side - Login form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <Card className="w-full max-w-md animate-fade-in">
                    <CardHeader className="space-y-1">
                        <div className="lg:hidden mb-4">
                            <h1 className="text-2xl font-bold">Fa. Thut Manager</h1>
                        </div>
                        <CardTitle className="text-2xl">Anmelden</CardTitle>
                        <CardDescription>
                            Melden Sie sich mit Ihren Zugangsdaten an
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {deployLogout && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
                                Die App wurde aktualisiert. Bitte melden Sie sich erneut an.
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="name@firma.de"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="input-focus"
                                    data-testid="email-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Passwort</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="input-focus pr-10"
                                        data-testid="password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full btn-scale" 
                                disabled={loading}
                                data-testid="login-btn"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <LogIn className="h-4 w-4 mr-2" />
                                )}
                                Anmelden
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
